import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { SearchField } from "@legends/ui";
import type { ColDef } from "ag-grid-community";
import DataGrid from "./DataGrid";
import {
  fetchAnalyticsSummary,
  fetchMetrics,
  type AnalyticsSummary,
  type CaveMetric,
  type MetricsOverview,
} from "./api";
import { alertStyle, errMsg, monoLabelStyle } from "./sections";

// Admin-local money format with thousands separators (bug #6: revenue printed as
// $310595.00 with no separators). Kept local to the admin so the shared formatPrice
// (fan storefront) is untouched. Cents → "$310,595.00".
const formatMoney = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Super-admin metrics (ADR 0016): tickets sold + revenue, aggregate and per-cave. The
// per-cave breakdown is the Cave-themed AG Grid (ADR 0156) — read-only (no add/edit/
// delete), sorted + filterable; the aggregate cards + Engagement table stay as-is.

const statCard: CSSProperties = {
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-md)",
  padding: "14px 16px",
  minWidth: 130,
  flex: 1,
};
const statValue: CSSProperties = {
  fontFamily: "var(--font-display)",
  fontSize: "var(--fs-2xl)",
  fontWeight: 600,
  color: "var(--text-1)",
  fontVariantNumeric: "tabular-nums",
  fontFeatureSettings: '"tnum" 1',
  lineHeight: 1,
};
const statLabel: CSSProperties = {
  ...monoLabelStyle,
  fontSize: "var(--fs-3xs)",
  marginTop: 4,
};
const th: CSSProperties = {
  ...monoLabelStyle,
  textAlign: "left",
  padding: "7px 10px",
  borderBottom: "1px solid var(--border-strong)",
};
const td: CSSProperties = {
  padding: "8px 10px",
  borderBottom: "1px solid var(--border-faint)",
  fontSize: "var(--fs-body-2)",
  color: "var(--text)",
};

// Behavioural event names → friendly labels for the analytics table (ADR 0017).
const EVENT_LABELS: Record<string, string> = {
  user_registered: "Registrations",
  user_logged_in: "Logins",
  login_failed: "Failed logins",
  cave_viewed: "Cave views",
  cavecast_viewed: "Cavecast views",
  checkout_started: "Checkouts started",
  purchase_completed: "Purchases",
  checkout_failed: "Checkouts failed",
  live_joined: "Live joins",
  live_left: "Live leaves",
  playback_failed: "Playback failures",
};

function eventLabel(name: string): string {
  return EVENT_LABELS[name] ?? name;
}

export default function MetricsScreen() {
  const [data, setData] = useState<MetricsOverview | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null);
  const [includeInternal, setIncludeInternal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");

  const columns = useMemo<ColDef<CaveMetric>[]>(
    () => [
      {
        field: "caveName",
        headerName: "Cave",
        minWidth: 180,
        flex: 2,
        // Fall back to the id when a cave has no display name.
        valueGetter: (p) => p.data?.caveName || p.data?.caveId || "",
      },
      {
        field: "revenueCents",
        headerName: "Revenue",
        type: "numericColumn",
        minWidth: 120,
        // Thousands-separated (bug #6); plain value, not gold (gold table values
        // reassign to --text-1 — gold keeps only its three jobs).
        valueFormatter: (p) => formatMoney(Number(p.value ?? 0)),
      },
      { field: "paidTickets", headerName: "Tickets sold", type: "numericColumn", minWidth: 120 },
      { field: "ticketsHeld", headerName: "Tickets held", type: "numericColumn", minWidth: 120 },
      // Comped (free-grant) tickets + their foregone list-price value (ADR 0230).
      { field: "compedTickets", headerName: "Comped", type: "numericColumn", minWidth: 110 },
      {
        field: "compedValueCents",
        headerName: "Comped value",
        type: "numericColumn",
        minWidth: 130,
        valueFormatter: (p) => formatMoney(Number(p.value ?? 0)),
      },
    ],
    [],
  );

  useEffect(() => {
    fetchMetrics()
      .then(setData)
      .catch((err: unknown) => setError(errMsg(err)));
  }, []);

  useEffect(() => {
    fetchAnalyticsSummary(includeInternal)
      .then(setAnalytics)
      .catch(() => setAnalytics(null));
  }, [includeInternal]);

  if (error) {
    return (
      <p role="alert" style={alertStyle}>
        {error}
      </p>
    );
  }
  if (!data) {
    return <p style={{ fontSize: "var(--fs-body)", color: "var(--text-dim)" }}>Establishing link…</p>;
  }

  const a = data.aggregate;
  const stats = [
    { label: "Revenue", value: formatMoney(a.revenueCents) },
    { label: "Tickets sold", value: String(a.paidTickets) },
    { label: "Tickets held", value: String(a.ticketsHeld) },
    { label: "Buyers", value: String(a.buyers) },
    { label: "Pending checkouts", value: String(a.pendingCheckouts) },
    // Comped (free-grant) tickets + foregone value (ADR 0230).
    { label: "Comped tickets", value: String(a.compedTickets) },
    { label: "Comped value", value: formatMoney(a.compedValueCents) },
  ];

  return (
    <div>
      <h1 style={{ margin: 0, ...monoLabelStyle, fontWeight: 600 }}>Legends · Metrics</h1>
      <p style={{ margin: "10px 0 0", fontSize: "var(--fs-body)", color: "var(--text-dim)" }}>
        Tickets sold and revenue — aggregate and per cave. Revenue counts paid Stripe checkouts only.
      </p>

      <section aria-label="Aggregate" style={{ marginTop: 18 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {stats.map((s) => (
            <div key={s.label} style={statCard}>
              <div style={statValue}>{s.value}</div>
              <div style={statLabel}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section aria-label="Per cave" style={{ marginTop: 22 }}>
        <div style={{ margin: "0 0 10px", display: "flex", alignItems: "center", gap: 12 }}>
          <h2 style={{ ...monoLabelStyle, margin: 0 }}>By cave</h2>
          <div style={{ marginLeft: "auto" }}>
            <SearchField value={q} onChange={setQ} placeholder="Filter caves…" width={280} />
          </div>
        </div>
        <DataGrid<CaveMetric>
          rows={data.perCave}
          columns={columns}
          getRowId={(r) => r.caveId}
          quickFilterText={q}
          label="Per-cave revenue"
          emptyText="No caves yet."
        />
      </section>

      <section aria-label="Engagement" style={{ marginTop: 26 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h2 style={{ ...monoLabelStyle, margin: 0 }}>Engagement</h2>
          <label
            style={{
              marginLeft: "auto",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: "var(--fs-2xs)",
              color: "var(--text-dim)",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={includeInternal}
              onChange={(e) => setIncludeInternal(e.target.checked)}
              aria-label="Include internal traffic"
            />
            Include staff traffic
          </label>
        </div>
        <p style={{ margin: "8px 0 0", fontSize: "var(--fs-2xs)", color: "var(--text-dim)" }}>
          Behavioural events across the platform. Staff/operator and impersonation traffic is excluded
          unless toggled on.
        </p>
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 10 }}>
          <thead>
            <tr>
              <th style={th}>Event</th>
              <th style={{ ...th, textAlign: "right" }}>Count</th>
              <th style={{ ...th, textAlign: "right" }}>Users</th>
            </tr>
          </thead>
          <tbody>
            {(analytics?.events ?? []).map((e) => (
              <tr key={e.name}>
                <td style={td}>{eventLabel(e.name)}</td>
                <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono)" }}>{e.count}</td>
                <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono)" }}>{e.users}</td>
              </tr>
            ))}
            {(analytics?.events?.length ?? 0) === 0 && (
              <tr>
                <td style={{ ...td, color: "var(--text-dim)" }} colSpan={3}>
                  No events recorded yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
